import type { HelpContent } from './types';

export const projectDetailsHelp: HelpContent = {
  pageTitle: 'Project Details',
  description: 'The Project Details page gives you a full view of a single project — its health, financials, team, time, expenses, purchases, and more — all organised across a set of tabs.',

  functionality: {
    overview:
      'Once you open a project, you land on the Project Details page. From here you can review and manage every aspect of the project across a series of tabs. The tabs available to you will depend on your access permissions — if you expect to see a tab that is not visible, contact your Administrator.',
    keyFeatures: [
      {
        label: 'Tab-based navigation',
        detail: 'Each area of the project (financials, billing, budgets, time, team, etc.) has its own dedicated tab, keeping information focused and easy to find.',
      },
      {
        label: 'Customisable tab visibility',
        detail: 'Choose which tabs are visible and reorder them to suit the way you work. Your preferences are saved automatically.',
      },
      {
        label: 'Project health indicator',
        detail: 'A colour-coded health status (green, amber, red) is shown in the project header, giving you an at-a-glance signal of overall project health.',
      },
      {
        label: 'Edit project details',
        detail: 'Update the project code, name, description, and status directly from the project header. Further project information such as client and dates can be edited from the Overview tab.',
        requiredPermission: 'projectActions.edit',
      },
      {
        label: 'Permission-based tab visibility',
        detail: 'Budget, billing, and financial tabs are only shown to users with the relevant access permissions. Tab availability will vary based on your access.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Project header',
        description: 'Displays the project code, name, description, and status.',
      },
      {
        name: 'Tab bar',
        description: 'The row of tabs beneath the project header. Click any tab to navigate to that area of the project. Use the more menu to show, hide, or reorder tabs.',
      },
      {
        name: 'Overview tab',
        description: 'A summary view of the project showing key metrics, health indicators, financial totals, and recent activity in one place.',
      },
      {
        name: 'Financials tab',
        description: 'A detailed breakdown of the project\'s financial position including revenue, costs, profitability, and margin.',
      },
      {
        name: 'Billing tab',
        description: 'Manage invoices and billing schedules for the project. View what has been billed, what is outstanding, and raise new invoices.',
      },
      {
        name: 'Sales Budget tab',
        description: 'View and manage the sales budget for the project, including planned revenue lines and actuals.',
        requiredPermission: 'projectActions.edit',
      },
      {
        name: 'Cost Budget tab',
        description: 'View and manage the cost budget for the project, including planned cost lines and actuals.',
        requiredPermission: 'projectActions.edit',
      },
      {
        name: 'Resource Budget tab',
        description: 'View and manage the resource budget for the project, including planned resource allocations and actuals.',
        requiredPermission: 'projectActions.edit',
      },
      {
        name: 'Cost Centres tab',
        description: 'View and manage the cost centre structure for the project. Useful for projects that split costs across departments or work streams.',
      },
      {
        name: 'Purchases tab',
        description: 'Review purchase requests and commitments against the project.',
      },
      {
        name: 'Time tab',
        description: 'View time entries logged against the project by team members. Filter by person, date range, or activity type.',
      },
      {
        name: 'Expenses tab',
        description: 'View expense claims submitted against the project.',
      },
      {
        name: 'Team tab',
        description: 'View the team members allocated to the project, their roles, and their allocation percentages.',
      },
      {
        name: 'Planner tab',
        description: 'Plan and manage resource scheduling for the project. Only visible when the resource planning tool is connected.',
      },
      {
        name: 'Audit tab',
        description: 'A full audit trail of changes made to the project, including who made the change and when.',
      },
      {
        name: 'Reports tab',
        description: 'Access project-specific reports and exports from within the project.',
      },
    ],
    useCases: [
      'Reviewing a project\'s financial position before a client meeting',
      'Checking which team members are allocated to a project',
      'Logging or reviewing time and expense entries against a project',
      'Managing invoices and billing schedules for a project',
      'Reviewing the full audit trail of changes to a project',
    ],
  },

  userGuide: {
    gettingStarted:
      'Open any project from the Projects list to land on the Project Details page. You will start on the Overview tab. Use the tab bar to navigate to different areas of the project. If you cannot see a tab you expect, check with your Administrator about your access permissions.',
    steps: [
      {
        title: 'How do I edit the project details?',
        items: [
          {
            text: 'In the project header, click the edit (pencil) icon next to the field you want to update.',
          },
          {
            text: 'Make your changes and save.',
            note: 'You must have edit permissions on the project to update its details.',
          },
        ],
        requiredPermission: 'projectActions.edit',
      },
      {
        title: 'How do I customise which tabs are shown?',
        items: [
          {
            text: 'Click the more menu at the right end of the tab bar.',
          },
          {
            text: 'Show, hide, or reorder individual tabs to suit the way you work.',
          },
          {
            text: 'Your tab preferences are saved automatically.',
          },
        ],
      },
      {
        title: 'How do I delete a project?',
        items: [
          {
            text: 'If you have delete access, a delete option is available in the project actions menu.',
          },
          {
            text: 'Select "Delete Project" and confirm the deletion.',
            note: 'Projects that are currently in use cannot be deleted. Deleted projects cannot be recovered.',
          },
        ],
        requiredPermission: 'projectActions.delete',
      },
    ],
    proTips: [
      'Use the Overview tab as your daily check-in for a project — it surfaces the most important metrics without you needing to visit each tab individually.',
      'If you regularly work with the same set of tabs, hide the ones you rarely use via the more menu to keep your workspace focused.',
      'The Audit tab is a useful first stop if something on a project looks unexpected — it shows a full history of changes with timestamps.',
    ],
  },

  faq: [
    {
      question: 'Why can\'t I see certain tabs on the project?',
      answer: 'Tab visibility is based on your access permissions. Budget and billing tabs are only shown to users with the relevant permissions. If you expect to see a tab that is not visible, contact your Administrator to review your access.',
    },
    {
      question: 'Why is the project showing a red health indicator?',
      answer: 'A red health indicator means the project has been flagged as at risk or in a critical state. This is usually set manually or triggered by threshold rules on key metrics. Check the Overview tab for more context.',
    },
    {
      question: 'Can I reorder the tabs?',
      answer: 'Yes. Click the more menu at the right end of the tab bar, then drag the tabs into the order you prefer. Your preferences are saved automatically.',
    },
    {
      question: 'Can I see the full history of changes to a project?',
      answer: 'Yes. The Audit tab provides a full chronological log of all changes made to the project, including the field changed, the old and new values, and who made the change.',
    },
    {
      question: 'Why can\'t I delete the project?',
      answer: 'Projects that are currently in use cannot be deleted. If the delete option is not available, the project may have active transactions, allocations, or dependencies. Contact your Administrator if you need assistance.',
    },
    {
      question: 'How do I get back to the Projects list?',
      answer: 'Click the back arrow in the project header, or click "Projects" in the left sidebar to return to the full project list.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Time Entries', path: '/time' },
    { label: 'Expenses', path: '/expenses' },
  ],
};
